import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumKegstarContainerType } from "./enums/enumKegstarContainerType.generated";

// Model Objects

export class KegstarSettings extends ModelObject<KegstarSettings> {

    public version: number = 0;
    /**
    * Whether the Kegstar link is enabled.
    */
    public isEnabled: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.isEnabled = jsonSerializedObject.isEnabled;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.isEnabled = this.isEnabled;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
    ];
}


export class KegstarReportLine extends ModelObject<KegstarReportLine> {

    /**
    * Company name
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public brewer: string = "";
    /**
    * Sales order
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public transactionType: string = "";
    /**
    * Outlet code
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerId: string = "";
    /**
    * Outlet business name
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerName: string = "";
    /**
    * Outlet main address 1
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerAddress1: string = "";
    /**
    * Outlet main address 2
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerAddress2: string = "";
    /**
    * Outlet main address 3
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerAddress3: string = "";
    /**
    * Outlet main address 4
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerCity: string = "";
    /**
    * Outlet main address 5
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerState: string = "";
    /**
    * Outlet main address 6
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerCountry: string = "";
    /**
    * Outlet main address postcode
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerPostcode: string = "";
    /**
    * Order despatch date
    */
    public shipDate: LocalDate = LocalDate.ofEpochDay(0);
    public item: EnumKegstarContainerType = EnumKegstarContainerType.none;
    /**
    * Quantity of the package type on the order
    */
    public quantity: number = 0;
    /**
    * Order number
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerReferenceNumber: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.brewer = jsonSerializedObject.brewer;
          this.transactionType = jsonSerializedObject.transactionType;
          this.customerId = jsonSerializedObject.customerId;
          this.customerName = jsonSerializedObject.customerName;
          this.customerAddress1 = jsonSerializedObject.customerAddress1;
          this.customerAddress2 = jsonSerializedObject.customerAddress2;
          this.customerAddress3 = jsonSerializedObject.customerAddress3;
          this.customerCity = jsonSerializedObject.customerCity;
          this.customerState = jsonSerializedObject.customerState;
          this.customerCountry = jsonSerializedObject.customerCountry;
          this.customerPostcode = jsonSerializedObject.customerPostcode;
          this.shipDate = LocalDate.parse(jsonSerializedObject.shipDate);
          this.item = EnumKegstarContainerType.getByValue(jsonSerializedObject.item, this.item);
          this.quantity = jsonSerializedObject.quantity;
          this.customerReferenceNumber = jsonSerializedObject.customerReferenceNumber;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.brewer = this.brewer;
        toRet.transactionType = this.transactionType;
        toRet.customerId = this.customerId;
        toRet.customerName = this.customerName;
        toRet.customerAddress1 = this.customerAddress1;
        toRet.customerAddress2 = this.customerAddress2;
        toRet.customerAddress3 = this.customerAddress3;
        toRet.customerCity = this.customerCity;
        toRet.customerState = this.customerState;
        toRet.customerCountry = this.customerCountry;
        toRet.customerPostcode = this.customerPostcode;
        toRet.shipDate = this.shipDate.toString();
        toRet.item = this.item.value;
        toRet.quantity = this.quantity;
        toRet.customerReferenceNumber = this.customerReferenceNumber;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class KegstarLinkService extends ServiceBase {

  public async getKegstarSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<KegstarSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new KegstarSettings(response.kegstarSettings);;
    return this.makeJsonRequest("kegstarLink/v1/getKegstarSettings", req, false, extractor, opts);
  };

  public async saveKegstarSettings(kegstarSettings: KegstarSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    kegstarSettings:kegstarSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("kegstarLink/v1/saveKegstarSettings", req, true, extractor, opts);
  };

  public async generateKegstarContainerReport(despatchStartDate: LocalDate, despatchEndDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(KegstarReportLine)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    despatchStartDate:despatchStartDate.toString(),
    despatchEndDate:despatchEndDate.toString(),
    };
    const extractor = (response:any) => response.kegstarReportLine.map((v: any) => new KegstarReportLine(v));;
    return this.makeJsonRequest("kegstarLink/v1/generateKegstarContainerReport", req, false, extractor, opts);
  };
}

export const kegstarLinkService = new KegstarLinkService();